import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { startWith, Subject } from 'rxjs';

import { SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetColumn, DatasetLinkValue } from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';

import { FindRecordPanelComponent } from '../../../find-record-panel/find-record-panel.component';
import { BlazeTableService } from '../../../shared/blaze-table.service';
import { BlazeTableRecord } from '../../../shared/blaze-table.store';
import { BaseCellInputComponent } from '../base-cell-input.component';

type LinkRecordDisplayValue = { linkRecordDisplayValue: string };
type BlazeTableRecordWithDisplay = BlazeTableRecord & LinkRecordDisplayValue;

@UntilDestroy()
@Component({
  selector: 'blaze-cell-input-link',
  templateUrl: './cell-input-link.component.html',
  styleUrls: ['./cell-input-link.component.scss', '../base-cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatIcon, MatMenuModule, FindRecordPanelComponent],
})
export class CellInputLinkComponent extends BaseCellInputComponent<
  DatasetLinkValue | DatasetLinkValue[]
> {
  private readonly schemaService = inject(SchemaService);
  private readonly blazeTableService = inject(BlazeTableService);

  @Input() readonly = false;
  @Input() columns: DatasetColumn[];

  @ViewChild('stringInput') stringInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('linkAddButton') linkAddButton!: ElementRef<HTMLDivElement>;

  linkFormControl = new FormControl<BlazeTableRecordWithDisplay[] | null>(null);

  recordMenuOpened$ = new Subject<boolean>();
  recordMenuTrigger!: MatMenuTrigger;

  override get nativeElement(): HTMLInputElement | null {
    return this.stringInputRef?.nativeElement || null;
  }

  get isMultiValueLink(): boolean {
    return this.column.multipleValues || false;
  }

  get linkedRecords(): BlazeTableRecordWithDisplay[] {
    return this.linkFormControl.value || [];
  }

  setOrRemoveLinkedRecord(record: BlazeTableRecord | null): void {
    if (!record) {
      return;
    }
    if (this.isMultiValueLink && this.linkedRecords) {
      const idx = this.linkedRecords?.findIndex(
        (linkedRecord) =>
          (linkedRecord?.value as DatasetLinkValue)?.rowId ===
          (record?.value as DatasetLinkValue)?.rowId,
      );
      const newLinkedRecords: BlazeTableRecordWithDisplay[] | null = [...this.linkedRecords];
      newLinkedRecords.splice(idx, 1);
      this.formControl.setValue(
        newLinkedRecords.length ? newLinkedRecords.map((l) => l.value) : null,
      );
    } else {
      this.formControl.setValue(null);
    }
    safeDetectChanges(this.cdr);
    setTimeout(() => this.linkAddButton?.nativeElement.click());
  }

  setOrAddLinkedRecord(record: BlazeTableRecord): void {
    let recordToAdd: BlazeTableRecord = record;
    if (!this.schemaService.hasExpandableLinks(this.column?.link?.tableId || '')) {
      const value = recordToAdd.value as DatasetLinkValue;
      recordToAdd = {
        value: value?.rowId,
        displayColumn: '',
        displayValue: '',
        tableHasExpandableLinks: false,
      };
    }
    if (this.isMultiValueLink) {
      const linkedRecords = [...(this.linkedRecords || []), recordToAdd];
      this.formControl.setValue(linkedRecords.map((l) => l.value as DatasetLinkValue));
    } else {
      this.formControl.setValue(recordToAdd.value as DatasetLinkValue);
    }
    if (!this.column.multipleValues) {
      this.recordMenuTrigger?.closeMenu();
    }
    safeDetectChanges(this.cdr);
  }

  setRecordMenuTrigger(trig: MatMenuTrigger): void {
    this.recordMenuTrigger = trig;
    this.recordMenuOpened$.next(true);
  }

  override executeExtraStartAction(): void {
    if (this.extraStartAction?.type === 'link-add') {
      afterNextRender(
        () => {
          this.linkAddButton?.nativeElement?.click();
        },
        { injector: this.injector },
      );
    } else if (this.extraStartAction?.type === 'link-remove') {
      const linkValue = { value: this.extraStartAction.link } as BlazeTableRecord;
      this.setOrRemoveLinkedRecord(linkValue);
    }
  }

  protected override createFormControl(): FormControl<DatasetLinkValue[] | null> {
    let valueArray: DatasetLinkValue[] | null;

    if (Array.isArray(this.value)) {
      valueArray = this.value as DatasetLinkValue[];
    } else if (this.value) {
      valueArray = [this.value as DatasetLinkValue];
    } else {
      valueArray = null;
    }
    const formControl = new FormControl({
      value: valueArray,
      disabled: this.isDisabled,
    });
    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.emitValueChanged(value);
    });
    formControl.valueChanges
      .pipe(startWith(valueArray), untilDestroyed(this))
      .subscribe((value) => {
        if (!value) {
          this.linkFormControl.setValue([]);
          return;
        }

        const records = (Array.isArray(value) ? value : [value]).map((v) =>
          this.createLinkedRecord(v),
        );
        this.linkFormControl.setValue(records);
      });
    return formControl;
  }

  private getLinkRecordDisplayValue(linkedRecord: BlazeTableRecord | null): string {
    if (!linkedRecord) {
      return '';
    }
    return this.blazeTableService.getLinkDisplayValue(linkedRecord);
  }

  private createLinkedRecord(value: DatasetLinkValue): BlazeTableRecordWithDisplay {
    const linkedRecord: BlazeTableRecord = {
      displayColumn: '',
      displayValue: '',
      value: value,
      tableHasExpandableLinks: false,
    };
    return {
      ...linkedRecord,
      linkRecordDisplayValue: this.getLinkRecordDisplayValue(linkedRecord),
    };
  }
}
